import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ImgView from './imgView';
import RMTabs from 'components/customComponents/Controls/RMTabs';
import { ITabItem } from 'components/tab';
import WellInfo from './wellInfo';
import AdditivesInfo from './additivesInfo';
import CrystalsInfo from './crystalsInfo';
import ProteinInfo from './proteinInfo';
import {
  useIsWellDropOrXtalSearch,
  useLatestSelectedSearchRow,
} from 'redux/hooks/searchnFilter';
import { useWellAndDropDetails } from '../hooks';
import { ISearchResultsRowMap, fieldsMap } from '../utils';

interface InfoTabWrapperProps {
  WrappedComp: (props) => JSX.Element;
  compProps?: Record<string, any>;
  contentMaxHeightClass: string;
}
const InfoTabWrapper = (props: InfoTabWrapperProps) => {
  const { WrappedComp, compProps = {}, contentMaxHeightClass } = props;
  return (
    <div className={`${contentMaxHeightClass} overflow-y-auto top-wrapper`}>
      <WrappedComp {...compProps} />
    </div>
  );
};

const InfoContainer = (props) => {
  const latestSelectedRow = useLatestSelectedSearchRow();
  const isWellDropOrXtalSearch = useIsWellDropOrXtalSearch();
  const [hasImage, setHasImage] = useState(false);

  const onImageAvailable = useCallback((available: boolean) => {
    setHasImage(available);
  }, []);

  useEffect(() => {
    if (!(isWellDropOrXtalSearch && latestSelectedRow?.['Capture Result.Profile Name'])) {
      setHasImage(false);
    }
  }, [isWellDropOrXtalSearch, latestSelectedRow]);

  const keyMap = useMemo(() => {
    let mapping: ISearchResultsRowMap = undefined;

    if (latestSelectedRow) {
      mapping = {};
      Object.entries(fieldsMap).forEach(([key, value]) => {
        mapping[key] = latestSelectedRow[value];
      });
      mapping['regionName'] = 'Drop';
    }
    return mapping;
  }, [latestSelectedRow]);

  const tabProps = useWellAndDropDetails();

  // With image (~28vh): constrain tab body so it can scroll.
  // Without image: use the freed vertical space.
  const contentMaxHeightClass = hasImage ? 'max-h-[46vh]' : 'max-h-[74vh]';

  const tabComponents: ITabItem[] = useMemo(() => {
    if (!latestSelectedRow || !tabProps) {
      return [];
    }

    const { proteinProps, wellProps, additivesProps } = tabProps;
    return [
      {
        title: 'Well',
        eventKey: 'well',
        content: (
          <InfoTabWrapper
            WrappedComp={WellInfo}
            compProps={{ ingredientWellProps: wellProps }}
            contentMaxHeightClass={contentMaxHeightClass}
          />
        ),
      },
      {
        title: 'Protein',
        eventKey: 'protein',
        content: (
          <InfoTabWrapper
            WrappedComp={ProteinInfo}
            compProps={{ compProps: proteinProps }}
            contentMaxHeightClass={contentMaxHeightClass}
          />
        ),
      },
      {
        title: 'Additives',
        eventKey: 'additives',
        content: (
          <InfoTabWrapper
            WrappedComp={AdditivesInfo}
            compProps={{ compProps: additivesProps }}
            contentMaxHeightClass={contentMaxHeightClass}
          />
        ),
      },
      {
        title: 'Crystals',
        eventKey: 'crystal',
        content: (
          <InfoTabWrapper
            WrappedComp={CrystalsInfo}
            compProps={{ regionId: latestSelectedRow['Region.ID'] }}
            contentMaxHeightClass={contentMaxHeightClass}
          />
        ),
      },
    ];
  }, [latestSelectedRow, tabProps, contentMaxHeightClass]);

  return (
    <div id="searchandfilter-infocontainer-wrapper" className="flex flex-col h-full">
      {isWellDropOrXtalSearch && latestSelectedRow?.['Capture Result.Profile Name'] && (
        <ImgView latestSelectedRow={keyMap} onImageAvailable={onImageAvailable} />
      )}
      <div id="searchandfilter-infocontainer-tabs">
        <RMTabs
          tabItems={tabComponents}
          defaultActiveKey={'well'}
          id={'searchandfilter-infocontainer-infoPanelTab'}
        />
      </div>
    </div>
  );
};

export default InfoContainer;
